import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Coupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecommerce/coupons');
      setCoupons(response.data.coupons || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/coupons/new');
  };

  const handleEdit = (coupon) => {
    navigate(`/coupons/${coupon.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await api.delete(`/ecommerce/coupons/${id}`);
      setSuccess('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Coupons</span>
      </nav>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              🎟️ Coupons
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage discount codes and promotions
            </p>
          </div>
          <button onClick={handleCreate} className="btn btn-primary">➕ Add Coupon</button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
            {success}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Code</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Discount</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Uses</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Valid Until</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>No coupons found</td></tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>{coupon.code}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {coupon.discount_type === 'percentage' 
                      ? `${coupon.discount_value}%` 
                      : `$${parseFloat(coupon.discount_value).toFixed(2)}`
                    }
                  </td>
                  <td style={{ padding: '0.75rem' }}>{coupon.times_used || 0} / {coupon.max_uses || '∞'}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className={`badge badge-${coupon.is_active ? 'success' : 'secondary'}`}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button onClick={() => handleEdit(coupon)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem' }}>Edit</button>
                    <button onClick={() => handleDelete(coupon.id)} className="btn btn-sm btn-danger">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Coupons;
